import React from 'react';
import { Spin, Image } from 'antd';
import { useDrop } from 'react-dnd';
import useNotification from '@hooks/notification/useNotification';
import StateEnum from '@constants/state.enum';
import { useTranslation } from 'next-i18next';
import { postHireApplicant } from '@services/applicants/applicants-http.service';
import HiredApplicantCard from '@components/app-components/position/position-steps/hire-card/hired-applicant-card/HiredApplicantCard';
import { StepApplicantElement } from '@models/applicants';
import styles from './HireCard.module.scss';

type HireCardProps = {
  candidates: Array<StepApplicantElement>;
  hiredLoader: boolean;
  hiredCounts: number;
  positionId: string;
  fetchApplicants: () => void;
};
const HireCard: React.FC<HireCardProps> = ({ candidates, positionId, fetchApplicants, hiredCounts, hiredLoader }) => {
  const { t: translate } = useTranslation('common');

  const [generalError] = useNotification(
    StateEnum.ERROR,
    'Something went wrong',
    'Oops, something went wrong, please try again later!',
  );

  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      accept: 'card',
      drop: async (card: { id: string; step: string }) => {
        try {
          await postHireApplicant(positionId, card.id.toString());
          await fetchApplicants();
        } catch (e: any) {
          generalError();
        }
      },
      collect: (monitor) => ({
        canDrop: monitor.canDrop(),
        isOver: monitor.isOver(),
      }),
    }),
    [],
  );

  const hoverColumnState = () => {
    if (isOver)
      return (
        <div className={styles.isOver} ref={drop}>
          <Image preview={false} src="/assets/images/conffeti-motion.gif" alt="conffeti" />
        </div>
      );
    return (
      <div className={styles.canDrop} ref={drop}>
        <Image preview={false} src="/assets/images/conffeti.png" alt="conffeti" />
      </div>
    );
  };

  return (
    <div className={styles.wrapStepColumn}>
      <div className={styles.wrapStepHeader}>
        <p className={`h6 ${styles.stepHeader}`}>{translate('hired')}</p>
        <div className={styles.columnCounter}>
          <div className={`small-caps-regular ${styles.columnCounter}`}>{hiredLoader ? <Spin /> : hiredCounts}</div>
        </div>
      </div>
      <div className={styles.stepColumn}>
        {canDrop ? (
          hoverColumnState()
        ) : (
          <div className={styles.wrapStepBody} ref={drop}>
            {candidates ? (
              candidates.map((applicant: StepApplicantElement, i, arr) => (
                <div key={applicant.id} className={arr.length - 1 === i ? styles.lastCard : ''}>
                  <HiredApplicantCard
                    applicant={applicant}
                    applicants={candidates}
                    fetchApplicants={() => fetchApplicants()}
                  />
                </div>
              ))
            ) : (
              <div />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HireCard;
